export const GROUP_TRIP_NOTICE_TYPES = Object.freeze([
  'PARTY_ADDITION_APPROVED',
  'APPLICATION_RECEIVED',
  'APPLICATION_APPROVED',
  'APPLICATION_REJECTED',
  'APPLICATION_WITHDRAWN',
  'PARTICIPANT_REMOVED',
  'PARTICIPANT_LEFT',
  'GROUP_TRIP_CANCELLED',
  'GROUP_TRIP_STARTING_SOON',
]);

export const GROUP_TRIP_NOTICE_STATUSES = Object.freeze(['UNREAD', 'READ']);

/** 僅保存顯示層所需的 presentation metadata——不是優先序引擎或 preference UI。 */
export const GROUP_TRIP_NOTICE_PRESENTATION = Object.freeze({
  PARTY_ADDITION_APPROVED: Object.freeze({ classification: 'EVENT_NOTIFICATION', importance: 'STANDARD_LIFECYCLE' }),
  APPLICATION_RECEIVED: Object.freeze({
    classification: 'EVENT_NOTIFICATION',
    importance: 'STANDARD_LIFECYCLE',
  }),
  APPLICATION_APPROVED: Object.freeze({
    classification: 'EVENT_NOTIFICATION',
    importance: 'STANDARD_LIFECYCLE',
  }),
  APPLICATION_REJECTED: Object.freeze({
    classification: 'EVENT_NOTIFICATION',
    importance: 'STANDARD_LIFECYCLE',
  }),
  APPLICATION_WITHDRAWN: Object.freeze({
    classification: 'EVENT_NOTIFICATION',
    importance: 'STANDARD_LIFECYCLE',
  }),
  PARTICIPANT_REMOVED: Object.freeze({
    classification: 'EVENT_NOTIFICATION',
    importance: 'CRITICAL_LIFECYCLE',
  }),
  PARTICIPANT_LEFT: Object.freeze({
    classification: 'EVENT_NOTIFICATION',
    importance: 'STANDARD_LIFECYCLE',
  }),
  GROUP_TRIP_CANCELLED: Object.freeze({
    classification: 'EVENT_NOTIFICATION',
    importance: 'CRITICAL_LIFECYCLE',
  }),
  GROUP_TRIP_STARTING_SOON: Object.freeze({
    classification: 'SCHEDULED_REMINDER',
    importance: 'REMINDER',
  }),
});

export function asIdString(value) {
  if (value == null || value === '') return '';
  return String(value);
}

export function isPositiveId(value) {
  return /^[1-9]\d*$/.test(asIdString(value));
}

export function isUnread(notice) {
  return String(notice?.noticeStatus || '') === 'UNREAD';
}

export function isKnownGroupTripNoticeType(noticeType) {
  return GROUP_TRIP_NOTICE_TYPES.includes(String(noticeType || ''));
}

/**
 * 類型標籤的 i18n key。未知且非空的 type 解析為 UNKNOWN
 * （永不為空），避免呼叫端把 raw enum 當主標籤顯示。
 */
export function noticeTypeMessageKey(noticeType) {
  const key = String(noticeType || '');
  if (!key) return '';
  if (GROUP_TRIP_NOTICE_TYPES.includes(key)) {
    return `travel.groupTripNotice.types.${key}`;
  }
  return 'travel.groupTripNotice.types.UNKNOWN';
}

export function noticeMessageKey(noticeType) {
  const key = String(noticeType || '');
  if (GROUP_TRIP_NOTICE_TYPES.includes(key)) {
    return `travel.groupTripNotice.messages.${key}`;
  }
  return '';
}

/**
 * 依角色組裝的內部 deep-link。applicationId 可選（legacy／非申請類 notice）。
 * 永不組外部或 protocol-relative URL。
 * GROUP_TRIP_STARTING_SOON → 公開詳情頁（契約已凍結）。
 */
export function noticeDeepLink(groupTripId, noticeType, applicationId) {
  if (!isPositiveId(groupTripId)) return '';
  const appQuery = isPositiveId(applicationId)
    ? `?applicationId=${asIdString(applicationId)}`
    : '';
  if (noticeType === 'APPLICATION_RECEIVED' || noticeType === 'APPLICATION_WITHDRAWN') {
    return `/group-trips/${asIdString(groupTripId)}${appQuery}`;
  }
  if (noticeType === 'PARTICIPANT_LEFT') {
    return `/group-trips/${asIdString(groupTripId)}`;
  }
  if (noticeType === 'APPLICATION_APPROVED' || noticeType === 'APPLICATION_REJECTED') {
    return `/public/group-trips/${asIdString(groupTripId)}${appQuery}`;
  }
  // STARTING_SOON、CANCELLED、REMOVED、unknown → 公開詳情頁
  return `/public/group-trips/${asIdString(groupTripId)}`;
}

export function normalizeGroupTripNotice(value, t) {
  const noticeId = asIdString(value?.noticeId);
  const groupTripId = asIdString(value?.groupTripId);
  const applicationId = isPositiveId(value?.applicationId)
    ? asIdString(value.applicationId)
    : '';
  const noticeType = String(value?.noticeType || '');
  const noticeStatus = String(value?.noticeStatus || 'UNREAD');
  const titleSnapshot = String(value?.titleSnapshot || '').trim();
  const known = isKnownGroupTripNoticeType(noticeType);
  const presentation = known ? GROUP_TRIP_NOTICE_PRESENTATION[noticeType] : null;

  const typeKey = noticeTypeMessageKey(noticeType);
  let typeLabel = '';
  if (typeof t === 'function' && typeKey) {
    typeLabel = t(typeKey);
  } else if (known) {
    typeLabel = noticeType;
  } else if (noticeType) {
    typeLabel = 'UNKNOWN';
  }

  const messageKey = noticeMessageKey(noticeType);
  const message = typeof t === 'function' && messageKey ? t(messageKey) : '';

  return {
    noticeId,
    groupTripId,
    applicationId: applicationId || null,
    noticeType,
    titleSnapshot,
    noticeStatus,
    readAt: value?.readAt || null,
    createTime: value?.createTime || null,
    unread: noticeStatus === 'UNREAD',
    typeLabel,
    message,
    classification: presentation?.classification || null,
    importance: presentation?.importance || null,
    deepLink: noticeDeepLink(groupTripId, noticeType, applicationId || null),
  };
}

function normalizeNoticeCursor(payload) {
  const createTime = payload?.nextCursorCreateTime;
  const noticeId = asIdString(payload?.nextCursorNoticeId);
  if (!createTime || !isPositiveId(noticeId)) return null;
  return { cursorCreateTime: String(createTime), cursorNoticeId: noticeId };
}

export function normalizeGroupTripNoticePage(payload, t) {
  const items = Array.isArray(payload?.items)
    ? payload.items
        .map((item) => normalizeGroupTripNotice(item, t))
        .filter((item) => isPositiveId(item.noticeId))
    : [];
  return {
    items,
    hasMore: Boolean(payload?.hasMore),
    nextCursor: normalizeNoticeCursor(payload),
  };
}

export function normalizeGroupTripNoticeUnreadCount(payload) {
  const raw = payload?.unreadCount;
  const count = Number.isFinite(Number(raw)) ? Math.max(0, Number(raw)) : 0;
  return { unreadCount: count };
}

export function mergeGroupTripNotice(list, noticeId, patch) {
  return (Array.isArray(list) ? list : []).map((item) => (
    String(item.noticeId) === String(noticeId) ? { ...item, ...patch } : item
  ));
}

/** 將列表中未讀項目本地標為已讀（API 成功後的 deterministic 更新）。 */
export function markAllGroupTripNoticesRead(list) {
  return (Array.isArray(list) ? list : []).map((item) => (
    item.unread
      ? {
        ...item,
        noticeStatus: 'READ',
        unread: false,
        readAt: item.readAt || new Date().toISOString(),
      }
      : item
  ));
}
